import { Alert, Button, Typography } from 'antd';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { colors } from '@/theme/tokens';
import { OBLIGATION_STATE_LABEL, type ObligationEvidence, type ObligationReading, type ObligationState } from '@/api/runEvidence';
import type { FindingReview, ValidationResultEvidence } from '@/api/validationRuns';
import { CAUSE_WORDS } from './FindingReviewPanel';

const { Text } = Typography;
const MONO = "'JetBrains Mono', monospace";

// ─────────────────────────────────────────────────────────────────────────────
// "What went wrong" as expected against observed (SI-03b, screen 07 frame B). One row per check of the scenario
// version that ran, with its own state — Pass, Fail, Not observed, Not assessed — so a reader sees what held, what
// failed and what was never looked at. Indeterminate and Not applicable are not drawn: this run has no semantic judge
// and no reviewed applicability condition, and the server's note says so. An execution error is kept apart from an
// agent failure. The cause shown is the finding's recorded classification, by a person, if any: no AI cause is
// invented here (the diagnostic step comes later).
// ─────────────────────────────────────────────────────────────────────────────

const TONE: Record<ObligationState, PillTone> = { Pass: 'green', Fail: 'red', NotObserved: 'slate', NotAssessed: 'slate', AwaitingReview: 'ink', NotApplicable: 'slate' };
const ORDER: ObligationState[] = ['Fail', 'AwaitingReview', 'NotObserved', 'NotAssessed', 'NotApplicable', 'Pass'];

export function ObligationsPanel({
  evidence,
  review,
  isDark,
  onOpenTurn,
}: {
  evidence: ValidationResultEvidence;
  review: FindingReview | null | undefined;
  isDark: boolean;
  onOpenTurn: (turnIndex: number) => void;
}) {
  const reading = evidence.obligations;
  const failedChecks = reading?.obligations.filter((o) => o.state === 'Fail').length ?? 0;
  const executionError = !!evidence.traceError && failedChecks === 0;
  const title = evidence.passed ? 'What was checked' : 'What went wrong';

  return (
    <div style={card(isDark, !evidence.passed && !executionError)} data-testid="what-went-wrong">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <Text strong style={{ fontSize: 14 }}>{title}</Text>
        {reading?.versionRead && <Text type="secondary" style={{ fontSize: 12 }}>{tally(reading)}</Text>}
      </div>

      {evidence.traceError && (
        <Alert
          type={executionError ? 'warning' : 'error'}
          showIcon
          style={{ marginBottom: 10 }}
          title={executionError ? 'The run did not complete this scenario: an execution error, not an agent failure' : 'The conversation also did not complete'}
          description={
            <span>
              <span style={{ fontFamily: MONO, fontSize: 12 }}>{evidence.traceError}</span>
              {executionError && ' No check failed on a turn that ran, so this result counts as Not assessed — neither a pass nor the agent’s failure.'}
            </span>
          }
        />
      )}

      {reading ? (
        <>
          {reading.obligations.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[...reading.obligations]
                .map((o, i) => ({ o, n: i + 1 }))
                .sort((a, b) => ORDER.indexOf(a.o.state) - ORDER.indexOf(b.o.state) || a.n - b.n)
                .map(({ o, n }) => (
                  <ObligationRow key={o.checkId} o={o} n={n} isDark={isDark} onOpenTurn={onOpenTurn} recordedTurn={evidence.turns.some((t) => t.turnIndex === o.turnIndex)} />
                ))}
            </div>
          )}
          {reading.unplaced.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <Text strong style={{ fontSize: 12.5 }}>Recorded failures not tied to one check</Text>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {reading.unplaced.map((u, i) => (
                  <li key={i} style={{ fontSize: 12.5 }}>
                    <Text type="secondary">{u.turnIndex != null ? `Turn ${u.turnIndex + 1}` : u.rule ? `Rule ${u.rule}` : 'Whole conversation'} · </Text>
                    <span style={{ fontFamily: MONO, fontWeight: 600 }}>{u.failure.check}</span> — {u.failure.detail}
                    <div style={{ color: colors.slate[500], fontSize: 11.5 }}>{u.why}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 11.5, color: isDark ? colors.slate[400] : colors.slate[500] }}>
            {reading.notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </>
      ) : (
        <LegacyFailures evidence={evidence} />
      )}

      {!evidence.passed && <CauseLine review={review} isDark={isDark} />}
    </div>
  );
}

function ObligationRow({
  o,
  n,
  isDark,
  onOpenTurn,
  recordedTurn,
}: {
  o: ObligationEvidence;
  n: number;
  isDark: boolean;
  onOpenTurn: (turn: number) => void;
  /** The turn has a record to open; a turn the run never reached has none. */
  recordedTurn: boolean;
}) {
  const failed = o.state === 'Fail';
  const border = failed ? `${colors.red[600]}88` : isDark ? colors.slate[700] : colors.slate[200];
  const label = (t: string) => (
    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.slate[500], marginBottom: 2 }}>{t}</div>
  );
  return (
    <div style={{ border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }} data-testid={`obligation-${o.state}`}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          padding: '8px 10px',
          background: failed ? (isDark ? `${colors.red[700]}22` : `${colors.red[100]}88`) : 'transparent',
        }}
      >
        <StatusPill tone={TONE[o.state]}>{OBLIGATION_STATE_LABEL[o.state]}</StatusPill>
        <Text strong style={{ fontSize: 13, flex: '1 1 200px', minWidth: 0 }}>
          {n} · {o.scope === 'Conversation' ? `Rule ${o.rule ?? ''}`.trim() : `Turn ${(o.turnIndex ?? 0) + 1}`} ·{' '}
          <span style={{ fontFamily: MONO, fontWeight: 500, fontSize: 12 }}>{o.operator}</span>
        </Text>
        <StatusPill tone="slate">{o.strength === 'Weak' ? 'Deterministic · phrase check' : 'Deterministic'}</StatusPill>
        {!o.requirementRef && <StatusPill tone="amber">Pending SME review</StatusPill>}
      </div>
      <div className="si-ob-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12, padding: '8px 10px 10px' }}>
        <div>
          {label('Expected')}
          <div style={{ fontSize: 13, lineHeight: 1.5 }}>{o.expected}</div>
        </div>
        <div>
          {label('Observed')}
          <div style={{ fontSize: 13, lineHeight: 1.5, color: failed ? (isDark ? colors.red[100] : colors.red[700]) : undefined, wordBreak: 'break-word' }}>{o.observed}</div>
          {o.turnIndex != null && o.scope !== 'Conversation' && o.state !== 'Pass' && recordedTurn && (
            <Button type="link" size="small" style={{ paddingLeft: 0, height: 'auto' }} onClick={() => onOpenTurn(o.turnIndex!)}>
              Open turn {o.turnIndex + 1} in the Debugger
            </Button>
          )}
        </div>
      </div>
      <style>{`@media (max-width: 560px) { .si-ob-grid { grid-template-columns: minmax(0,1fr) !important; } }`}</style>
    </div>
  );
}

function CauseLine({ review, isDark }: { review: FindingReview | null | undefined; isDark: boolean }) {
  const text =
    review === undefined
      ? 'Reading the finding’s review…'
      : review?.cause
        ? `Cause, classified by a reviewer: ${CAUSE_WORDS[review.cause]}.`
        : 'Cause: not classified yet. A person classifies it under Review; no cause is suggested here.';
  return (
    <div
      style={{
        marginTop: 10,
        padding: '8px 10px',
        borderRadius: 8,
        background: isDark ? colors.slate[900] : colors.slate[50],
        border: `1px dashed ${isDark ? colors.slate[600] : colors.slate[300]}`,
        fontSize: 12.5,
      }}
      data-testid="finding-cause"
    >
      {text}
    </div>
  );
}

function LegacyFailures({ evidence }: { evidence: ValidationResultEvidence }) {
  const failures = [
    ...evidence.turns.flatMap((t) => t.failures.map((f) => ({ where: `Turn ${t.turnIndex + 1}`, f }))),
    ...evidence.conversationRules.filter((r) => !r.passed).flatMap((r) => r.failures.map((f) => ({ where: 'Whole conversation', f }))),
  ];
  if (evidence.passed) return <Text>Every recorded check passed.</Text>;
  if (failures.length === 0) return <Text type="secondary">The result failed, but no failed check was recorded.</Text>;
  return (
    <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {failures.map((x, i) => (
        <li key={i}>
          <Text type="secondary">{x.where} · </Text>
          <Text style={{ fontFamily: MONO, fontWeight: 600 }}>{x.f.check}</Text>
          <div>{x.f.detail}</div>
        </li>
      ))}
    </ul>
  );
}

function tally(reading: ObligationReading): string {
  const n = reading.obligations.length;
  const count = (s: ObligationState) => reading.obligations.filter((o) => o.state === s).length;
  const parts = ORDER.map((s) => [count(s), OBLIGATION_STATE_LABEL[s].toLowerCase()] as const).filter(([c]) => c > 0).map(([c, l]) => `${c} ${l}`);
  return `${n} check${n === 1 ? '' : 's'}${parts.length ? ' · ' + parts.join(' · ') : ''}`;
}

function card(isDark: boolean, failed: boolean): React.CSSProperties {
  return {
    padding: '12px 14px',
    borderRadius: 12,
    background: isDark ? colors.slate[800] : '#FFFFFF',
    border: `1px solid ${failed ? `${colors.red[600]}88` : isDark ? colors.slate[700] : colors.slate[200]}`,
  };
}
